import { useState, useCallback, useMemo } from 'react';
import type { CatalogStack, CreateCatalogStackRequest, UpdateCatalogStackRequest, EndpointTargetMode } from '@/types/catalog';
import { parseEnvText, formatEnvText } from '@/lib/envParser';

const DEFAULT_COMPOSE = `services:
  app:
    image: nginx:alpine
    ports:
      - "8080:80"
    environment:
      - TZ=UTC
    restart: unless-stopped
`;

interface StackFormData {
  name: string;
  compose_content: string;
  cpu_limit: number;
  memory_mb: number;
  storage_gb: number;
  shared_cpu: boolean;
  env_vars: { key: string; value: string }[];
  target_mode: EndpointTargetMode;
  locked_endpoints: number[];
  security_block_overrides: Record<number, boolean>; // blockId -> explicit toggle state (only overrides stored, sparse)
}

interface ValidationError {
  field: string;
  message: string;
}

// Build sparse security-block overrides from a stack's security_blocks list.
// Only blocks whose `selection` is explicitly set (true/false) become overrides;
// blocks with undefined selection rely on their is_enabled_default.
function deriveSecurityBlockOverrides(blocks?: { id: number; selection?: boolean }[]): Record<number, boolean> {
  if (!blocks) return {};
  const overrides: Record<number, boolean> = {};
  for (const b of blocks) {
    if (b.selection !== undefined) {
      overrides[b.id] = b.selection;
    }
  }
  return overrides;
}

// The values of a brand-new stack. Used as the dirty baseline until the stack has been saved once.
function blankFormData(): StackFormData {
  return {
    name: '',
    compose_content: DEFAULT_COMPOSE,
    cpu_limit: 1,
    memory_mb: 512,
    storage_gb: 10,
    shared_cpu: false,
    env_vars: [],
    target_mode: null,
    locked_endpoints: [],
    security_block_overrides: {},
  };
}

// Form state as it is stored on the server for a stack (the dirty-tracking baseline).
function stackToFormData(stack: CatalogStack): StackFormData {
  return {
    name: stack.name,
    compose_content: stack.compose_content,
    cpu_limit: stack.cpu_limit,
    memory_mb: stack.memory_mb,
    storage_gb: stack.storage_gb,
    shared_cpu: stack.shared_cpu,
    env_vars: Object.entries(stack.env_vars || {}).map(([key, value]) => ({ key, value })),
    target_mode: stack.target_mode ?? null,
    locked_endpoints: stack.locked_endpoints ?? [],
    security_block_overrides: deriveSecurityBlockOverrides(stack.security_blocks),
  };
}

export function useStackForm(initialStack?: CatalogStack | null) {
  // An existing stack is its own baseline from the first render, so an untouched edit form is not dirty.
  const [formData, setFormData] = useState<StackFormData>(() =>
    initialStack
      ? stackToFormData(initialStack)
      : blankFormData(),
  );

  // Null for a new stack: dirty then means "has any content".
  const [originalData, setOriginalData] = useState<StackFormData | null>(() =>
    initialStack ? stackToFormData(initialStack) : null,
  );

  const [isAdvancedMode, setIsAdvancedMode] = useState(false);
  const [rawEnvText, setRawEnvTextState] = useState(() => {
    const vars = initialStack?.env_vars
      ? Object.entries(initialStack.env_vars).map(([key, value]) => ({ key, value }))
      : [];
    return formatEnvText(vars);
  });
  const [advancedErrors, setAdvancedErrors] = useState<string[]>([]);

  // Wrap setRawEnvText to parse and validate
  const setRawEnvText = useCallback((text: string) => {
    setRawEnvTextState(text);
    const { envVars, errors } = parseEnvText(text);
    setAdvancedErrors(errors);
    if (errors.length === 0) {
      setFormData(prev => ({ ...prev, env_vars: envVars }));
    }
  }, []);

  const setAdvancedMode = useCallback((active: boolean) => {
    if (active) {
      // Simple -> Advanced: format current env vars to string
      const text = formatEnvText(formData.env_vars);
      setRawEnvTextState(text);
      setAdvancedErrors([]);
      setIsAdvancedMode(true);
    } else {
      // Advanced -> Simple: only allow if no errors
      const { envVars, errors } = parseEnvText(rawEnvText);
      if (errors.length > 0) {
        setAdvancedErrors(errors);
        return; // Block toggle if there are errors
      }
      setFormData(prev => ({ ...prev, env_vars: envVars }));
      setAdvancedErrors([]);
      setIsAdvancedMode(false);
    }
  }, [formData.env_vars, rawEnvText]);

  // Initialize original data for dirty tracking
  const initializeOriginal = useCallback((stack: CatalogStack) => {
    const data = stackToFormData(stack);
    setFormData(data);
    setOriginalData(data);
    setIsAdvancedMode(false);
    setRawEnvTextState(formatEnvText(data.env_vars));
    setAdvancedErrors([]);
  }, []);

  // After a successful save the current values become the baseline. Pass the form's own state: the
  // server's stack record may not reflect the request (e.g. security-block selections).
  const markSaved = useCallback(() => {
    setOriginalData(formData);
  }, [formData]);

  // Discard unsaved edits: back to the last saved state (or empty for a new stack).
  const discardChanges = useCallback(() => {
    if (!originalData) {
      setFormData(blankFormData());
      setRawEnvTextState('');
    } else {
      setFormData(originalData);
      setRawEnvTextState(formatEnvText(originalData.env_vars));
    }
    setIsAdvancedMode(false);
    setAdvancedErrors([]);
  }, [originalData]);

  // Reset form to initial state
  const resetForm = useCallback(() => {
    setFormData(blankFormData());
    setOriginalData(null);
    setIsAdvancedMode(false);
    setRawEnvTextState('');
    setAdvancedErrors([]);
  }, []);

  // Update individual fields
  const setName = useCallback((name: string) => {
    setFormData(prev => ({ ...prev, name }));
  }, []);

  const setComposeContent = useCallback((content: string) => {
    setFormData(prev => ({ ...prev, compose_content: content }));
  }, []);

  const setCpuLimit = useCallback((limit: number) => {
    setFormData(prev => ({ ...prev, cpu_limit: limit }));
  }, []);

  const setMemoryMb = useCallback((mb: number) => {
    setFormData(prev => ({ ...prev, memory_mb: mb }));
  }, []);

  const setStorageGb = useCallback((gb: number) => {
    setFormData(prev => ({ ...prev, storage_gb: gb }));
  }, []);

  const setSharedCPU = useCallback((enabled: boolean) => {
    setFormData(prev => ({ ...prev, shared_cpu: enabled }));
  }, []);

  const setEnvVars = useCallback((vars: { key: string; value: string }[]) => {
    setFormData(prev => ({ ...prev, env_vars: vars }));
  }, []);

  const addEnvVar = useCallback(() => {
    setFormData(prev => ({
      ...prev,
      env_vars: [...prev.env_vars, { key: '', value: '' }],
    }));
  }, []);

  const removeEnvVar = useCallback((index: number) => {
    setFormData(prev => ({
      ...prev,
      env_vars: prev.env_vars.filter((_, i) => i !== index),
    }));
  }, []);

  const updateEnvVar = useCallback((index: number, field: 'key' | 'value', value: string) => {
    setFormData(prev => ({
      ...prev,
      env_vars: prev.env_vars.map((v, i) => (i === index ? { ...v, [field]: value } : v)),
    }));
  }, []);

  // Target mode setters
  const setTargetMode = useCallback((mode: EndpointTargetMode) => {
    setFormData(prev => ({ ...prev, target_mode: mode }));
  }, []);

  const setLockedEndpoints = useCallback((ids: number[]) => {
    setFormData(prev => ({ ...prev, locked_endpoints: ids }));
  }, []);

  const setTargetModeInherit = useCallback(() => {
    setFormData(prev => ({ ...prev, target_mode: null, locked_endpoints: [] }));
  }, []);

  // Toggle a security block. Only stores an override when the toggle differs
  // from the block's is_enabled_default (sparse). Toggling back to default
  // removes the override.
  const setSecurityBlockEnabled = useCallback((blockId: number, enabled: boolean, isDefault: boolean) => {
    setFormData(prev => {
      const next = { ...prev.security_block_overrides };
      if (enabled === isDefault) {
        delete next[blockId];
      } else {
        next[blockId] = enabled;
      }
      return { ...prev, security_block_overrides: next };
    });
  }, []);

  // Validation
  const validate = useCallback((): ValidationError[] => {
    const errors: ValidationError[] = [];

    // Name validation
    if (!formData.name.trim()) {
      errors.push({ field: 'name', message: 'Name is required' });
    } else if (!/^[a-zA-Z0-9_-]+$/.test(formData.name)) {
      errors.push({ field: 'name', message: 'Name must contain only letters, numbers, underscores, and dashes' });
    }

    // CPU validation
    if (formData.cpu_limit < 0.01 || formData.cpu_limit > 16) {
      errors.push({ field: 'cpu_limit', message: 'CPU limit must be between 0.01 and 16' });
    }

    // Memory validation
    if (formData.memory_mb < 64 || formData.memory_mb > 32768) {
      errors.push({ field: 'memory_mb', message: 'Memory must be between 64 and 32768 MB' });
    }

    // Storage validation
    if (formData.storage_gb < 1 || formData.storage_gb > 1000) {
      errors.push({ field: 'storage_gb', message: 'Storage must be between 1 and 1000 GB' });
    }

    // Compose content validation (basic YAML check)
    if (!formData.compose_content.trim()) {
      errors.push({ field: 'compose_content', message: 'Compose content is required' });
    } else {
      // Check if YAML has at least a services key
      const hasServices = formData.compose_content.includes('services:');
      if (!hasServices) {
        errors.push({ field: 'compose_content', message: 'Compose file must have a services section' });
      }
    }

    // Env var key validation
    formData.env_vars.forEach((env) => {
      if (env.key.trim() && !/^[A-Za-z_][A-Za-z0-9_]*$/.test(env.key)) {
        errors.push({ field: 'env_vars', message: `Env var key "${env.key}" is invalid (must be valid env name)` });
      }
    });

    return errors;
  }, [formData]);

  // Check if form has changes
  // Every editable field is compared against the baseline: the saved stack, or blank for a new one.
  const isDirty = useMemo(() => {
    const base = originalData ?? blankFormData();
    return (
      formData.name !== base.name ||
      formData.compose_content !== base.compose_content ||
      formData.cpu_limit !== base.cpu_limit ||
      formData.memory_mb !== base.memory_mb ||
      formData.storage_gb !== base.storage_gb ||
      formData.shared_cpu !== base.shared_cpu ||
      JSON.stringify(formData.env_vars) !== JSON.stringify(base.env_vars) ||
      formData.target_mode !== base.target_mode ||
      JSON.stringify(formData.locked_endpoints) !== JSON.stringify(base.locked_endpoints) ||
      JSON.stringify(formData.security_block_overrides) !== JSON.stringify(base.security_block_overrides)
    );
  }, [formData, originalData]);

  // Build request payload
  const buildRequest = useCallback((): CreateCatalogStackRequest | UpdateCatalogStackRequest => {
    const envVarsRecord: Record<string, string> = {};
    formData.env_vars.forEach(({ key, value }) => {
      if (key.trim()) {
        envVarsRecord[key.trim()] = value;
      }
    });

    const security_block_selections = Object.entries(formData.security_block_overrides).map(
      ([id, enabled]) => ({ security_block_id: Number(id), is_enabled: enabled })
    );

    return {
      name: formData.name.trim(),
      compose_content: formData.compose_content,
      cpu_limit: formData.cpu_limit,
      memory_mb: formData.memory_mb,
      storage_gb: formData.storage_gb,
      shared_cpu: formData.shared_cpu,
      env_vars: Object.keys(envVarsRecord).length > 0 ? envVarsRecord : undefined,
      target_mode: formData.target_mode,
      locked_endpoints: formData.locked_endpoints.length > 0 ? formData.locked_endpoints : undefined,
      // Always send an explicit array (even empty) so the backend clears stale
      // opt-out opinions when the user reverts all toggles back to defaults.
      // An empty array = "clear all overrides"; omitted would mean "untouched".
      security_block_selections: security_block_selections,
    };
  }, [formData]);

  return {
    formData,
    originalData,
    initializeOriginal,
    markSaved,
    discardChanges,
    resetForm,
    setName,
    setComposeContent,
    setCpuLimit,
    setMemoryMb,
    setStorageGb,
    setSharedCPU,
    setEnvVars,
    addEnvVar,
    removeEnvVar,
    updateEnvVar,
    setTargetMode,
    setLockedEndpoints,
    setTargetModeInherit,
    setSecurityBlockEnabled,
    validate,
    isDirty,
    buildRequest,
    isAdvancedMode,
    rawEnvText,
    advancedErrors,
    setRawEnvText,
    setAdvancedMode,
  };
}